/*
  <otw-tabs> — tabbed collections (PRD 8.1 row 13).
  Tab buttons carry data-url / data-title. Clicking a tab shows skeletons, fetches
  `{url}?section_id=otw-tabbed-collections-items&limit=N`, caches the HTML per tab, fades the swap (200ms),
  and updates the "View All {title}" link. Arrow keys / Home / End move between tabs.
*/
(() => {
  if (customElements.get('otw-tabs')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FADE = 200;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class OtwTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panel = this.querySelector('[data-panel]');
      this.viewAll = this.querySelector('[data-view-all-link]');
      if (!this.tabs.length || !this.panel) return;
      this.sectionId = this.dataset.sectionId || 'otw-tabbed-collections-items';
      this.limit = this.dataset.limit || '10';
      this.cache = new Map();
      this.active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.cache.set(this.active, this.panel.innerHTML);
      this.token = 0;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) this.select(tab);
      });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;
        const index = this.tabs.indexOf(tab);
        let next = -1;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        if (next < 0) return;
        event.preventDefault();
        this.tabs[next].focus();
        this.select(this.tabs[next]);
      });
    }

    skeletons() {
      const card = `<div class="otw-slider__slide"><div class="otw-card otw-card--skeleton otw-card--standard" aria-hidden="true">
        <div class="otw-card__media-wrap"><div class="otw-skeleton otw-skeleton--media otw-card__skeleton-media"></div></div>
        <div class="otw-card__body">
          <span class="otw-skeleton otw-skeleton--text otw-card__skeleton-line otw-card__skeleton-line--short"></span>
          <span class="otw-skeleton otw-skeleton--text otw-card__skeleton-line"></span>
          <span class="otw-skeleton otw-skeleton--text otw-card__skeleton-line otw-card__skeleton-line--price"></span>
          <span class="otw-skeleton otw-card__skeleton-btn"></span>
        </div></div></div>`;
      return `<otw-slider aria-hidden="true"><div class="otw-slider__track">${card.repeat(5)}</div></otw-slider>`;
    }

    setActive(tab) {
      this.tabs.forEach((item) => {
        const on = item === tab;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
        item.tabIndex = on ? 0 : -1;
      });
      this.panel.setAttribute('aria-labelledby', tab.id);
      if (this.viewAll) {
        const label = (this.dataset.viewAll || 'View All [title]').replace('[title]', tab.dataset.title || '');
        this.viewAll.textContent = label;
        this.viewAll.setAttribute('href', tab.dataset.url);
      }
      const scroller = tab.closest('.otw-tabs__scroll');
      if (scroller) {
        const left = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
        scroller.scrollTo({ left, behavior: reduced.matches ? 'auto' : 'smooth' });
      }
    }

    async swap(html) {
      const animate = !reduced.matches;
      if (animate) {
        this.panel.style.transition = `opacity ${FADE}ms ease`;
        this.panel.style.opacity = '0';
        await wait(FADE);
      }
      this.panel.innerHTML = html;
      if (animate) {
        this.panel.style.opacity = '1';
        await wait(FADE);
        this.panel.style.transition = '';
        this.panel.style.opacity = '';
      }
    }

    async load(tab) {
      const url = new URL(tab.dataset.url, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('limit', this.limit);
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`otw-tabs: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.getElementById(`shopify-section-${this.sectionId}`) || doc.body;
      return root.innerHTML;
    }

    async select(tab) {
      if (tab === this.active) return;
      this.active = tab;
      const token = ++this.token;
      this.setActive(tab);
      this.panel.setAttribute('aria-busy', 'true');

      try {
        if (!this.cache.has(tab)) {
          this.panel.innerHTML = this.skeletons();
          const html = await this.load(tab);
          if (token !== this.token) return;
          this.cache.set(tab, html);
        }
        if (token !== this.token) return;
        await this.swap(this.cache.get(tab));
      } catch (error) {
        if (token !== this.token) return;
        this.panel.innerHTML = '';
        const link = document.createElement('a');
        link.href = tab.dataset.url;
        link.className = 'otw-btn otw-btn--outline';
        link.textContent = this.viewAll ? this.viewAll.textContent : tab.dataset.title;
        this.panel.appendChild(link);
      } finally {
        if (token === this.token) this.panel.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('otw-tabs', OtwTabs);
})();
